import {
  Button,
  Chip,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  SharedSelection,
  Textarea,
} from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useHeroToast } from 'src/hooks';
import { useUpdateClientRequestFirestore } from 'src/queries';
import { ClientRequestFirestoreResponse, ClientRequestStatus } from 'src/types';

import {
  getStatusMeta,
  REPLY_CHANNEL_COLOR,
  REPLY_CHANNEL_LABEL,
  STATUS_OPTIONS,
} from '../../constants';

interface ClientRequestModalProps {
  request: ClientRequestFirestoreResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (request: ClientRequestFirestoreResponse): string => {
  const ts = request.createdAt;
  if (ts?.toDate) return ts.toDate().toLocaleString('pl-PL');
  return request.timestamp || '';
};

const InfoRow: FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase tracking-wider text-gray-400">{label}</span>
    <span className="text-sm text-gray-800">{value || '—'}</span>
  </div>
);

export const ClientRequestModal: FC<ClientRequestModalProps> = ({ request, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useHeroToast();
  const { mutate, isPending } = useUpdateClientRequestFirestore();

  const [status, setStatus] = useState<ClientRequestStatus | undefined>(request?.status);
  const [reply, setReply] = useState('');
  const [pendingAction, setPendingAction] = useState<'status' | 'reply' | null>(null);

  useEffect(() => {
    setStatus(request?.status);
    setReply('');
    setPendingAction(null);
  }, [request?.id, request?.status]);

  const replies = useMemo(
    () => [...(request?.replies ?? [])].sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() ?? 0;
      const bTime = b.createdAt?.toMillis?.() ?? 0;
      return aTime - bTime;
    }),
    [request?.replies],
  );

  if (!request) return null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['client-request'] });

  const statusChanged = status !== undefined && status !== request.status;

  const handleStatusSave = () => {
    if (!statusChanged || !status) return;
    setPendingAction('status');
    mutate(
      { id: request.id, data: { status } },
      {
        onSuccess: () => {
          showSuccess(
            request.userEmail
              ? `Status zmieniony. Powiadomienie wysłano na ${request.userEmail}.`
              : 'Status zmieniony (brak adresu e-mail użytkownika).',
          );
          refresh();
        },
        onError: () => showError('Nie udało się zmienić statusu zgłoszenia.'),
        onSettled: () => setPendingAction(null),
      },
    );
  };

  const handleReplySend = () => {
    const trimmed = reply.trim();
    if (!trimmed) return;
    if (!request.userEmail) {
      showError('Zgłoszenie nie ma adresu e-mail użytkownika — nie można wysłać odpowiedzi.');
      return;
    }
    setPendingAction('reply');
    mutate(
      { id: request.id, data: { reply: trimmed } },
      {
        onSuccess: () => {
          showSuccess(`Odpowiedź wysłano na ${request.userEmail} i zapisano w zgłoszeniu.`);
          setReply('');
          refresh();
        },
        onError: () => showError('Nie udało się wysłać odpowiedzi.'),
        onSettled: () => setPendingAction(null),
      },
    );
  };

  const currentMeta = getStatusMeta(request.status);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      radius="sm"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <span className="text-base font-semibold">{request.subject || 'Zgłoszenie'}</span>
            <Chip size="sm" color={currentMeta.color} variant="flat" radius="sm">
              {currentMeta.label}
            </Chip>
          </div>
          <span className="text-xs font-normal text-gray-400">{formatDate(request)}</span>
        </ModalHeader>

        <ModalBody className="gap-5">
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="Hotel" value={request.hotelName} />
            <InfoRow label="Kategoria" value={request.category} />
            <InfoRow label="Użytkownik" value={request.userName} />
            <InfoRow label="E-mail" value={request.userEmail} />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wider text-gray-400">Opis</span>
            <p className="whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-sm text-gray-800">
              {request.description || '—'}
            </p>
          </div>

          {request.attachments && request.attachments.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wider text-gray-400">Załączniki</span>
              <div className="flex flex-wrap gap-2">
                {request.attachments.map((att) => (
                  <a
                    key={att.fileUrl}
                    href={att.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-primary hover:bg-gray-50"
                  >
                    <Paperclip size={12} />
                    {att.fileName || 'Załącznik'}
                  </a>
                ))}
              </div>
            </div>
          )}

          <Divider />

          {/* Zmiana statusu */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-gray-700">Status zgłoszenia</span>
            <div className="flex items-end gap-2">
              <Select
                size="sm"
                aria-label="Status zgłoszenia"
                className="max-w-[220px]"
                selectedKeys={status ? [status] : []}
                onSelectionChange={(keys: SharedSelection) => {
                  const value = Array.from(keys as Set<string>)[0] as ClientRequestStatus | undefined;
                  setStatus(value);
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.key}>{opt.label}</SelectItem>
                ))}
              </Select>
              <Button
                size="sm"
                color="primary"
                radius="sm"
                isDisabled={!statusChanged}
                isLoading={isPending && pendingAction === 'status'}
                onPress={handleStatusSave}
              >
                Zmień status
              </Button>
            </div>
            <span className="text-xs text-gray-400">
              Zmiana statusu wyśle do użytkownika e-mail z powiadomieniem.
            </span>
          </div>

          <Divider />

          {/* Historia odpowiedzi */}
          {replies.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-semibold text-gray-700">
                Odpowiedzi (
                {replies.length}
                )
              </span>
              <div className="flex flex-col gap-2">
                {replies.map((item, idx) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${item.createdAt?.toMillis?.() ?? idx}-${idx}`}
                    className="rounded-md border-l-4 border-success bg-gray-50 p-3"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                        {item.author || 'Zespół Wsparcia'}
                        {item.channel && (
                          <Chip size="sm" variant="flat" radius="sm" color={REPLY_CHANNEL_COLOR[item.channel] ?? 'default'}>
                            {REPLY_CHANNEL_LABEL[item.channel] ?? item.channel}
                          </Chip>
                        )}
                      </span>
                      <span className="text-xs text-gray-400">
                        {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString('pl-PL') : ''}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-gray-800">{item.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nowa odpowiedź */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-gray-700">Odpowiedz użytkownikowi</span>
            <Textarea
              minRows={4}
              radius="sm"
              placeholder={
                request.userEmail
                  ? `Odpowiedź zostanie wysłana na ${request.userEmail}`
                  : 'Brak adresu e-mail użytkownika'
              }
              value={reply}
              onValueChange={setReply}
              isDisabled={!request.userEmail}
            />
            <span className="text-xs text-gray-400">
              Odpowiedź zostanie zapisana w zgłoszeniu i wysłana e-mailem na adres użytkownika.
            </span>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button size="sm" variant="light" radius="sm" onPress={onClose}>
            Zamknij
          </Button>
          <Button
            size="sm"
            color="secondary"
            radius="sm"
            isDisabled={!reply.trim() || !request.userEmail}
            isLoading={isPending && pendingAction === 'reply'}
            onPress={handleReplySend}
          >
            Wyślij odpowiedź
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
